import React, { useState } from 'react';
import { BlueprintVersion } from '@/lib/stores/storeTypes';
import { formatVersion, getTimeAgo, getVersionBadgeColor, determineBumpType } from '@/lib/utils/versionUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, User, FileText, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/user-feedback/tooltip';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { VersionSeverityBadge } from './VersionSeverityBadge';
import { BulkUpdateDialog } from './BulkUpdateDialog';
import { BreakingChangesDialog } from './BreakingChangesDialog';
import { VersionChangesModal } from './VersionChangesModal';

interface VersionCardProps {
  version: BlueprintVersion;
  previousVersion?: BlueprintVersion;
  onView?: (version: BlueprintVersion) => void;
  onRestore?: (version: BlueprintVersion) => void;
  isRestoring?: boolean;
  currentVersion?: { major: number; minor: number; patch: number };
  onRefresh?: () => void;
}

export function VersionCard({ version, previousVersion, onView, onRestore, isRestoring = false, currentVersion, onRefresh }: VersionCardProps) {
  const { getUserNameById } = useSimplifiedUserData();
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [showBreakingChangesDialog, setShowBreakingChangesDialog] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  
  const versionString = formatVersion({
    major: version.major_version,
    minor: version.minor_version,
    patch: version.patch_version
  });

  // Check if this version is the current active version
  // Also check for synthetic current version ID
  const isCurrentVersion = (currentVersion 
    && currentVersion.major === version.major_version 
    && currentVersion.minor === version.minor_version 
    && currentVersion.patch === version.patch_version)
    || version.id?.startsWith('current-');

  // Get user name
  const userName = getUserNameById(version.created_by);

  // Determine bump type by comparing with previous version
  const bumpType = previousVersion
    ? determineBumpType(
        {
          major: previousVersion.major_version,
          minor: previousVersion.minor_version,
          patch: previousVersion.patch_version
        },
        {
          major: version.major_version,
          minor: version.minor_version,
          patch: version.patch_version
        }
      )
    : 'patch';

  const badgeColor = bumpType ? getVersionBadgeColor(bumpType) : 'bg-gray-100 text-gray-800 border-gray-200';

  // Handle severity badge click
  const handleSeverityClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking severity badge
    if (version.change_severity === 'breaking') {
      setShowBreakingChangesDialog(true);
    } else if (version.change_severity === 'review') {
      setShowBulkUpdateDialog(true);
    }
  };

  // Handle card click to show changes modal
  const handleCardClick = () => {
    setShowChangesModal(true);
  };

  // The count is now calculated in real-time by the backend
  const shouldShowBadge = version.change_severity && 
                          version.change_severity !== 'none' && 
                          version.affected_items_count > 0;

  return (
    <>
      <div 
        className="border border-gray-200 rounded p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Badge className={`${badgeColor} border text-xs font-mono whitespace-nowrap`}>
              v{versionString}
            </Badge>
            {shouldShowBadge && (
              <div 
                onClick={handleSeverityClick} 
                className="cursor-pointer"
              >
                <VersionSeverityBadge
                  severity={version.change_severity as any}
                  details={version.severity_details}
                  affectedCount={version.affected_items_count}
                  showIcon={true}
                />
              </div>
            )}
          </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isCurrentVersion && !shouldShowBadge ? (
            // Only show ACTIVE badge if current version AND no unresolved issues
            <Badge className="bg-green-100 text-green-800 border-green-200 border text-xs font-semibold whitespace-nowrap">
              ACTIVE
            </Badge>
          ) : (
            // Only show Restore button if not current AND no unresolved changes
            !isCurrentVersion && onRestore && !shouldShowBadge && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click when clicking restore
                  onRestore(version);
                }}
                className="h-6 w-auto whitespace-nowrap"
                disabled={isRestoring}
              >
                {isRestoring ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  'Restore'
                )}
              </Button>
            )
          )}
        </div>
      </div>



        <div className="flex items-center justify-between w-full text-xs text-gray-500 gap-2">
          <div className="flex items-center gap-1 flex-shrink-0">
            <Clock className="w-3 h-3" />
            <span className="whitespace-nowrap">{getTimeAgo(version.created_at)}</span>
          </div>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help min-w-0 flex-1 justify-end">
                  <User className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{userName}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{userName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
    </div>

    {/* Dialogs */}
    {version.blueprint_id && showBulkUpdateDialog && (
      <BulkUpdateDialog
        isOpen={showBulkUpdateDialog}
        onClose={() => setShowBulkUpdateDialog(false)}
        blueprintId={version.blueprint_id}
        version={{
          major: version.major_version,
          minor: version.minor_version,
          patch: version.patch_version
        }}
        onSuccess={() => {
          setShowBulkUpdateDialog(false);
          // Refresh version history to update counts
          if (onRefresh) {
            onRefresh();
          }
        }}
      />
    )}

    {version.blueprint_id && showBreakingChangesDialog && (
      <BreakingChangesDialog
        isOpen={showBreakingChangesDialog}
        onClose={() => setShowBreakingChangesDialog(false)}
        blueprintId={version.blueprint_id}
        version={{
          major: version.major_version,
          minor: version.minor_version,
          patch: version.patch_version
        }}
        severityDetails={version.severity_details}
      />
    )}

    {/* Version Changes Modal */}
    <VersionChangesModal
      isOpen={showChangesModal}
      onClose={() => setShowChangesModal(false)}
      version={version}
    />
    </>
  );
}

